import { FlexColumn, FlexRow, useMoment } from '@gorila-shared-ui/components';
import { Banner } from 'baseui/banner';
import { Checkbox } from 'baseui/checkbox';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { LabelSmall } from 'baseui/typography';
import { useStyles } from '../../hooks/useStyles';

type Props = {
  days?: {
    day: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    endHour?: number;
    startHour?: number;
    allDay: boolean;
  }[];
  onChange: (
    days?: {
      day: 0 | 1 | 2 | 3 | 4 | 5 | 6;
      endHour?: number;
      startHour?: number;
      allDay: boolean;
    }[],
  ) => void;
};
export function TimeByDayPicker({ days, onChange }: Readonly<Props>) {
  const { css, classes, theme, concatenateClasses } = useStyles();
  const { formatHourByNumber, convertHourToNumber } = useMoment();

  const dayLabel = {
    0: 'Lunes',
    1: 'Martes',
    2: 'Miercoles',
    3: 'Jueves',
    4: 'Viernes',
    5: 'Sabado',
    6: 'Domingo',
  };

  if (!days) return null;

  return (
    <FlexColumn>
      <FlexColumn
        gap={0}
        classNames={css({
          ...theme.borders.border300,
          borderRadius: theme.borders.popoverBorderRadius,
          padding: 0,
        })}
      >
        {days.map((day, index) => {
          return (
            <FlexRow
              key={`${dayLabel[day.day]}-row`}
              classNames={`${css({
                alignItems: 'center',
                minHeight: '40px',
                justifyContent: 'space-between',
                rowGap: theme.sizing.scale200,
                flexWrap: 'wrap',
                borderBottom: index < days.length - 1 ? `1px solid ${theme.borders.border300.borderColor}` : 'none',
                padding: `${theme.sizing.scale300} ${theme.sizing.scale600} ${theme.sizing.scale300} ${theme.sizing.scale200}`,
              })}`}
            >
              <FlexRow
                gap={theme.sizing.scale200}
                classNames={concatenateClasses(classes.centeredStart, css({ minWidth: '140px' }))}
              >
                <Checkbox
                  checked={days[index].allDay}
                  checkmarkType="toggle"
                  onChange={(e) => {
                    const d = days.map((day, i) => {
                      if (index !== i) return day;
                      if (!e.target.checked)
                        return {
                          ...day,
                          allDay: e.target.checked,
                          startHour: 8,
                          endHour: 24,
                        };
                      return { ...day, allDay: e.target.checked };
                    });
                    onChange([...d]);
                  }}
                  overrides={{
                    Label: {
                      style: ({ $checked, $theme }) => ({
                        position: 'relative',
                        color: `${$checked ? $theme.colors.backgroundInversePrimary : $theme.colors.tooltipText}`,
                        paddingLeft: '0',
                        left: `${$checked ? '-45px' : '-30px'}`,
                        fontSize: '9px',
                        lineHeight: '28px',
                        transition: 'left 200ms ease 0s',
                        width: 0,
                      }),
                    },
                    ToggleTrack: {
                      style: () => ({
                        width: '50px',
                        height: '16px',
                      }),
                    },
                    Toggle: {
                      style: ({ $checked }) => ({
                        transform: `${$checked ? 'translateX(150%)' : 'none'}`,
                      }),
                    },
                  }}
                  labelPlacement="right"
                >
                  24H
                </Checkbox>
                {dayLabel[day.day]}
              </FlexRow>
              {!days[index].allDay && (
                <FlexRow
                  centered
                  classNames={`${css({ minWidth: '320px' })}`}
                >
                  <LabelSmall>Desde:</LabelSmall>
                  <FormControl
                    error={day.startHour && day.startHour < 8}
                    overrides={{
                      ControlContainer: {
                        style: {
                          marginBottom: 0,
                        },
                      },
                      Caption: {
                        style: {
                          display: 'none',
                        },
                      },
                    }}
                  >
                    <Input
                      type="time"
                      size="compact"
                      value={formatHourByNumber(day.startHour || 8)}
                      onChange={({ target: { value: e } }) => {
                        const hour = e === '00:00' ? '24:00' : e;
                        const d = days.map((day, i) => {
                          if (index !== i) return day;
                          return { ...day, startHour: convertHourToNumber(hour) };
                        });
                        onChange([...d]);
                      }}
                      required
                    />
                  </FormControl>
                  <LabelSmall>hasta:</LabelSmall>
                  <FormControl
                    error={day.endHour && day.endHour < (day.startHour || 8)}
                    overrides={{
                      ControlContainer: {
                        style: {
                          marginBottom: 0,
                        },
                      },
                      Caption: {
                        style: {
                          display: 'none',
                        },
                      },
                    }}
                  >
                    <Input
                      type="time"
                      size="compact"
                      value={formatHourByNumber(day.endHour || 24)}
                      onChange={({ target: { value: e } }) => {
                        const hour = e === '00:00' ? '24:00' : e;
                        const d = days.map((day, i) => {
                          if (index !== i) return day;
                          return { ...day, endHour: convertHourToNumber(hour) };
                        });
                        onChange([...d]);
                      }}
                      required
                    />
                  </FormControl>
                </FlexRow>
              )}
            </FlexRow>
          );
        })}
      </FlexColumn>
      <Banner
        hierarchy="low"
        kind="warning"
        overrides={{
          Root: {
            style: {
              marginTop: 0,
              marginRight: 0,
              marginBottom: 0,
              marginLeft: 0,
              textAlign: 'center',
            },
          },
        }}
      >
        La hora mínima es a las 08:00 mientras que la hora máxima a las 24:00
      </Banner>
    </FlexColumn>
  );
}
